<template>
  <div v-if="interactData && show" class="interact-area">
    <div class="interact-actions">
      <button type="button" class="interact-button reward-button" @click="$emit('reward')">
        <AppIcon name="orangeCoin" size="md" aria-label="打赏" />
        <span>打赏</span>
      </button>
      <button
        type="button"
        class="interact-button like-button"
        :class="{ 'is-liked': interactData.isLike }"
        @click="$emit('like')"
      >
        <AppIcon
          name="like"
          size="md"
          :variant="interactData.isLike ? 'active' : 'default'"
          aria-label="点赞"
        />
        <div class="like-text-wrapper">
          <span>赞</span>
          <span class="count-text">{{ interactData.likeCount || 0 }}</span>
        </div>
      </button>
      <button
        type="button"
        class="interact-button collect-button"
        :class="{ 'is-collected': interactData.isCollect }"
        @click="$emit('collect')"
      >
        <AppIcon
          name="heart"
          size="md"
          :variant="interactData.isCollect ? 'active' : 'default'"
          aria-label="收藏"
        />
        <span>{{ interactData.isCollect ? '已收藏' : '收藏' }}</span>
      </button>
    </div>
    <div class="comment-section">
      <div class="comment-section-title">
        第{{ volNum }}卷精彩评论({{ commentTotal }})
      </div>
      <div class="comment-input-area">
        <img
          :src="avatarUrl"
          alt="用户头像"
          class="comment-input-avatar"
          @error="$emit('avatar-error', $event)"
        />
        <input
          :value="commentInput"
          class="comment-input"
          placeholder="发表评论..."
          @input="$emit('update:commentInput', $event.target.value)"
          @keyup.enter="$emit('submit-comment')"
        />
      </div>
      <div v-if="commentInput.trim()" class="comment-input-actions">
        <button type="button" class="comment-send-button" @click="$emit('submit-comment')">
          发送
        </button>
      </div>
      <div v-if="comments.length" class="comment-list">
        <div
          v-for="(comment, index) in comments"
          :key="comment.messageId || comment.id || index"
          class="comment-item"
          :class="{ 'has-divider': index < comments.length - 1 }"
          :id="commentElId(comment)"
        >
          <div class="comment-content-wrapper">
            <img
              :src="commentAvatarUrl(comment)"
              alt="用户头像"
              class="comment-avatar"
              @error="$emit('comment-avatar-error', $event)"
            />
            <div class="comment-main">
              <div class="comment-user-info">
                <span class="comment-author">{{ comment.userName || '' }}</span>
              </div>
              <div class="comment-content">{{ comment.content || '' }}</div>
              <div class="comment-actions">
                <span class="comment-time">{{ formatCommentTime(comment.commentTime) }}</span>
                <span
                  class="comment-action-item"
                  :class="{ 'is-liked': isCommentLiked(comment) }"
                  @click="$emit('comment-like', comment)"
                >
                  <AppIcon
                    name="like"
                    size="sm"
                    :variant="isCommentLiked(comment) ? 'active' : 'default'"
                    aria-label="点赞评论"
                  />
                  <span>{{ comment.likeCount || 0 }}</span>
                </span>
                <span
                  class="comment-action-item comment-reply-text-btn"
                  @click="toggleReplyBox(comment.messageId)"
                >
                  回复
                </span>
                <span
                  v-if="comment.isBelongMe"
                  class="vol-comment-delete-btn"
                  @click.stop="deleteVolComment(comment)"
                >
                  删除
                </span>
              </div>
              <div v-if="showReplyBoxMap[comment.messageId]" class="vol-comment-reply-box">
                <textarea
                  v-model="replyContentMap[comment.messageId]"
                  class="vol-comment-reply-input"
                  :placeholder="replyPlaceholder(comment)"
                />
                <div class="vol-comment-reply-box-actions">
                  <button
                    type="button"
                    class="vol-comment-reply-submit"
                    :disabled="submittingReplyMap[comment.messageId]"
                    @click="submitCommentReply(comment)"
                  >
                    {{ submittingReplyMap[comment.messageId] ? '提交中...' : '提交' }}
                  </button>
                  <button
                    type="button"
                    class="vol-comment-reply-cancel"
                    @click="toggleReplyBox(comment.messageId)"
                  >
                    取消
                  </button>
                </div>
              </div>
              <div
                v-if="commentHasReplies(comment)"
                class="vol-comment-expand-reply"
                :class="{ 'is-loading': pendingExpandRepliesMap[comment.messageId] }"
                @click="pendingExpandRepliesMap[comment.messageId] ? null : toggleCommentReplies(comment)"
              >
                <template v-if="pendingExpandRepliesMap[comment.messageId]">
                  <AppIcon name="refresh" size="sm" class="vol-reply-loading-icon app-icon--spin" aria-label="" />
                  加载中...
                </template>
                <template v-else>
                  {{ comment.showReplies ? '收起' : '展开' }}{{ comment.replyCount || 0 }}条回复
                </template>
              </div>
              <div
                v-if="comment.showReplies && comment.replies?.length"
                class="vol-comment-replies-list"
              >
                <div
                  v-for="reply in visibleReplies(comment)"
                  :key="reply.messageId || reply.id"
                  class="vol-comment-reply-item"
                >
                  <div class="vol-comment-reply-header">
                    <img
                      :src="commentAvatarUrl(reply)"
                      alt="用户头像"
                      class="vol-comment-reply-avatar"
                      @error="$emit('comment-avatar-error', $event)"
                    />
                    <span class="vol-comment-reply-author">{{ reply.userName || '' }}</span>
                    <span v-if="reply.toUserName" class="vol-comment-reply-to-info">
                      <span class="vol-comment-reply-to-text">回复</span>
                      <span class="vol-comment-reply-to-name">{{ reply.toUserName }}</span>
                    </span>
                  </div>
                  <p class="vol-comment-reply-content">{{ reply.content || '' }}</p>
                  <div class="vol-comment-reply-meta">
                    <span class="vol-comment-reply-time">
                      {{ formatCommentTime(reply.replyTime || reply.commentTime) }}
                    </span>
                    <span
                      class="vol-comment-reply-btn"
                      @click="toggleReplyBox(reply.messageId)"
                    >
                      回复
                    </span>
                    <span
                      v-if="reply.isBelongMe"
                      class="vol-comment-delete-btn"
                      @click.stop="deleteVolCommentReply(comment, reply)"
                    >
                      删除
                    </span>
                  </div>
                  <div v-if="showReplyBoxMap[reply.messageId]" class="vol-comment-reply-box is-nested">
                    <textarea
                      v-model="replyContentMap[reply.messageId]"
                      class="vol-comment-reply-input"
                      :placeholder="replyPlaceholder(reply)"
                    />
                    <div class="vol-comment-reply-box-actions">
                      <button
                        type="button"
                        class="vol-comment-reply-submit"
                        :disabled="submittingReplyMap[reply.messageId]"
                        @click="submitCommentReply(reply)"
                      >
                        {{ submittingReplyMap[reply.messageId] ? '提交中...' : '提交' }}
                      </button>
                      <button
                        type="button"
                        class="vol-comment-reply-cancel"
                        @click="toggleReplyBox(reply.messageId)"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="comment.replies.length > 3" class="vol-comment-reply-expand">
                  <span
                    class="vol-comment-reply-expand-btn"
                    @click="toggleShowAllReplies(comment.messageId)"
                  >
                    {{ showAllRepliesMap[comment.messageId] ? '收起' : '展开更多' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showCommentFooter" class="view-all-comments">
        <div class="comment-footer-inner">
          <button
            v-if="showLoadMoreButton"
            type="button"
            class="view-all-button"
            :disabled="isLoadingMore"
            @click="$emit('load-more')"
          >
            {{ isLoadingMore ? '加载中...' : '查看更多' }}
          </button>
          <span v-else class="view-all-end">已加载全部评论</span>
          <template v-if="showCollapseButton">
            <span class="comment-footer-divider" aria-hidden="true"></span>
            <button
              type="button"
              class="view-all-button comment-collapse-button"
              @click="$emit('collapse-comments')"
            >
              收起评论
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import AppIcon from '@/shared/components/AppIcon.vue'
import { isVolCommentLiked, VOL_INTERACT_COLLAPSED_COMMENT_LIMIT } from '@/features/comic/utils/normalizeVolInteract'

const props = defineProps({
  show: { type: Boolean, default: true },
  volNum: { type: [Number, String], required: true },
  interactData: { type: Object, default: null },
  commentInput: { type: String, default: '' },
  avatarUrl: { type: String, default: '' },
  isLoadingMore: { type: Boolean, default: false },
  commentElId: { type: Function, required: true },
  commentAvatarUrl: { type: Function, required: true },
  formatCommentTime: { type: Function, required: true },
  replyContentMap: { type: Object, default: () => ({}) },
  showReplyBoxMap: { type: Object, default: () => ({}) },
  submittingReplyMap: { type: Object, default: () => ({}) },
  pendingExpandRepliesMap: { type: Object, default: () => ({}) },
  showAllRepliesMap: { type: Object, default: () => ({}) },
  toggleReplyBox: { type: Function, required: true },
  submitCommentReply: { type: Function, required: true },
  toggleCommentReplies: { type: Function, required: true },
  toggleShowAllReplies: { type: Function, required: true },
  commentHasReplies: { type: Function, required: true },
  deleteVolComment: { type: Function, required: true },
  deleteVolCommentReply: { type: Function, required: true },
})

defineEmits([
  'reward',
  'like',
  'collect',
  'submit-comment',
  'comment-like',
  'load-more',
  'collapse-comments',
  'avatar-error',
  'comment-avatar-error',
  'update:commentInput',
])

/** @param {object} comment */
function isCommentLiked(comment) {
  return isVolCommentLiked(comment)
}

/**
 * 回复输入框占位文案
 * @param {{ userName?: string } | null | undefined} target
 * @returns {string}
 */
function replyPlaceholder(target) {
  const name = target?.userName?.trim() || ''
  return name ? `回复@${name}` : '回复'
}

/** @param {object} comment */
function visibleReplies(comment) {
  const replies = comment.replies || []
  if (props.showAllRepliesMap[comment.messageId]) return replies
  return replies.slice(0, 3)
}

const allComments = computed(
  () => props.interactData?.commentExtractVo?.commentVos || [],
)

const isCommentsCollapsed = computed(
  () => Boolean(props.interactData?.commentExtractVo?.isCollapsed),
)

const comments = computed(() => {
  if (!isCommentsCollapsed.value) return allComments.value
  return allComments.value.slice(0, VOL_INTERACT_COLLAPSED_COMMENT_LIMIT)
})

const commentTotal = computed(() => {
  const extract = props.interactData?.commentExtractVo
  if (!extract) return 0
  return Number(extract.contentTotal ?? 0)
})

const hasNext = computed(() => {
  const extract = props.interactData?.commentExtractVo
  if (!extract) return false
  if (typeof extract.hasNext === 'boolean') return extract.hasNext
  return commentTotal.value > allComments.value.length
})

const showLoadMoreButton = computed(() => {
  if (hasNext.value) return true
  return (
    isCommentsCollapsed.value &&
    allComments.value.length > VOL_INTERACT_COLLAPSED_COMMENT_LIMIT
  )
})

const showCollapseButton = computed(
  () =>
    !isCommentsCollapsed.value &&
    allComments.value.length > VOL_INTERACT_COLLAPSED_COMMENT_LIMIT,
)

const showCommentFooter = computed(
  () => commentTotal.value > 0 || allComments.value.length > 0,
)
</script>
